import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import * as Device from 'react-device-detect';

import { Logo } from '@components';
import { loginStatusVar } from '@context';
import LoginDialogForm from './login-form';
import { SignupDialog } from '../simple-signup';
import { AppleConnect, GoogleConnect, FacebookConnect } from './buttons';

interface LoginProps {
  redirect?: string | null;
}

const ConnexionAlert = () => {
  const loginStatus = useReactiveVar(loginStatusVar);
  const { t } = useTranslation('authentication');

  if (loginStatus.isSessionExpired) {
    return (
      <Alert severity="info" sx={{ width: '100%' }}>
        {t('alert.Session has expired')}
      </Alert>
    );
  } else if (loginStatus.shouldLogFirst) {
    return (
      <Alert severity="info" sx={{ width: '100%' }}>
        {t('alert.You first need to connect')}
      </Alert>
    );
  }
  return null;
};

export default function Login(props: Readonly<LoginProps>) {
  const { redirect = null } = props;
  const { isLandscape } = Device.useMobileOrientation();
  const { t } = useTranslation('authentication');

  const isLandscapeMobile = Device.isMobileOnly && isLandscape;
  const spacing = Device.isMobileOnly ? 1 : 2;

  const dividerSx = {
    fontSize: '0.8rem',
    width: '100%',
  };

  if (isLandscapeMobile) {
    return (
      <Stack
        direction="row"
        width="100%"
        height="100%"
        justifyContent="space-between"
        spacing={spacing}
        divider={<Divider orientation="vertical" variant="middle" flexItem />}
      >
        <Stack spacing={spacing} justifyContent="center" alignItems="center" flex={1}>
          <Logo clickable={false} size={2.5} />
        </Stack>
        <Stack
          p={spacing + 5}
          spacing={spacing + 1}
          justifyContent="center"
          alignItems="center"
          divider={<Divider orientation="horizontal" flexItem />}
          flex={1}
        >
          <AppleConnect redirect={redirect} />
          <GoogleConnect redirect={redirect} />
          <LoginDialogForm redirect={redirect} />
          <SignupDialog redirect={redirect} />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack width="100%" height="100%" spacing={spacing + 2} justifyContent="center" alignItems="center">
      <Stack py={3}>
        <Logo clickable={false} size={2.5} />
      </Stack>
      {Device.isDesktop && <ConnexionAlert />}
      <Stack spacing={spacing} width="100%">
        <AppleConnect redirect={redirect} />
        <GoogleConnect redirect={redirect} />
      </Stack>
      <Divider sx={dividerSx}>{t('text.or')}</Divider>
      <LoginDialogForm redirect={redirect} />
      <SignupDialog redirect={redirect} />
    </Stack>
  );
}
